// src/hooks/useClients.ts
import { useState, useEffect, useCallback } from 'react';
import { Client, NewClient } from '../types';
import { ClientService } from '../services/clientService';
import { useAuth } from './useAuth';

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token, user, isAuthenticated, loading: authLoading } = useAuth();

  const fetchClients = useCallback(async () => {
    // Wait for auth to be fully initialized
    if (authLoading || !isAuthenticated || !token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const clientsData = await ClientService.getClients(token);
      setClients(clientsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, authLoading]);

  const createClient = async (clientData: NewClient) => {
    if (!token || !user) throw new Error('Authentication required');
    
    try {
      await ClientService.createClient(clientData, token, user.id);
      await fetchClients(); // Refresh list
    } catch (err) {
      throw err;
    }
  };

  const updateClient = async (id: number, clientData: Partial<NewClient>) => {
    if (!token) throw new Error('Authentication required');
    
    try {
      await ClientService.updateClient(id, clientData, token);
      await fetchClients(); // Refresh list
    } catch (err) {
      throw err;
    }
  };

  const deleteClient = async (id: number) => {
    if (!token) throw new Error('Authentication required');
    
    try {
      await ClientService.deleteClient(id, token);
      await fetchClients(); // Refresh list
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return {
    clients,
    loading,
    error,
    createClient,
    updateClient,
    deleteClient,
    refetch: fetchClients,
  };
};